import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Löst ein einmaliges Retest aus: Site Audit + Keyword-Rankings frisch prüfen.
 * Danach werden alle relevanten Queries neu geladen, so dass Impact-Cards
 * die neuen Werte zeigen.
 */
export default function RetestAllButton({ shopId, onSuccess }) {
  const { t, lang } = useLang();
  const qc = useQueryClient();

  const retest = useMutation({
    mutationFn: async () => (await api.post(`/shops/${shopId}/retest-all`)).data,
    onSuccess: (data) => {
      const kw = data?.keywords || {};
      const audit = data?.audit || {};
      const src = kw.source === "dataforseo" ? "DataForSEO" : "Mock";
      toast.success(
        lang === "de"
          ? `Neu geprüft: ${kw.total} Keywords via ${src} · Audit-Score ${audit.score ?? "?"} (${audit.issues ?? 0} Issues)`
          : `Retested: ${kw.total} keywords via ${src} · audit score ${audit.score ?? "?"} (${audit.issues ?? 0} issues)`,
      );
      // Invalidate everything that could be affected
      qc.invalidateQueries({ queryKey: ["overview", shopId] });
      qc.invalidateQueries({ queryKey: ["keywords", shopId] });
      qc.invalidateQueries({ queryKey: ["audit", shopId] });
      qc.invalidateQueries({ queryKey: ["funnel", shopId] });
      qc.invalidateQueries({ queryKey: ["cro-actions", shopId] });
      qc.invalidateQueries({ queryKey: ["cro-impact", shopId] });
      qc.invalidateQueries({ queryKey: ["revenue-context", shopId] });
      qc.invalidateQueries({ queryKey: ["alerts", shopId] });
      qc.invalidateQueries({ queryKey: ["alerts-summary"] });
      // Notify parent (e.g. CRO page auto-regenerates recommendations)
      if (typeof onSuccess === "function") onSuccess(data);
    },
    onError: () => {
      toast.error(t("retest_error"));
    },
  });

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => retest.mutate()}
      disabled={!shopId || retest.isPending}
      data-testid="retest-all-button"
      className="gap-1.5"
      title={t("retest_hint")}
    >
      {retest.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" />
      )}
      {retest.isPending ? t("retest_running") : t("retest_button")}
    </Button>
  );
}
